import { useState } from 'react';
import { ArrowRight, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '@/core/auth-context';
import { BrandLogo } from '@/components/BrandLogo';
import { ThemeToggle } from '@/components/ThemeToggle';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';

function readSavedField(key: string): string {
  try {
    return localStorage.getItem(key) ?? '';
  } catch {
    return '';
  }
}

export function LoginScreen() {
  const { login } = useAuth();
  const [username, setUsername] = useState(() => readSavedField('saved_username'));
  const [password, setPassword] = useState('');
  const [apiKey, setApiKey] = useState(() => readSavedField('saved_admin_api_key'));
  const [rememberMe, setRememberMe] = useState(() => {
    const saved = readSavedField('saved_username');
    return saved !== '' ? true : true;
  });
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    const ok = login(username, password, { rememberMe, adminApiKey: apiKey || undefined });
    setLoading(false);
    if (!ok) {
      setError('Username o password non validi');
    }
  };

  return (
    <div className="min-h-[100dvh] bg-background flex flex-col relative overflow-hidden">
      <div className="pointer-events-none absolute -top-32 -right-32 w-80 h-80 bg-brand rounded-full blur-[100px] opacity-25" />
      <div className="pointer-events-none absolute -bottom-40 -left-32 w-80 h-80 bg-brand rounded-full blur-[120px] opacity-15" />

      <div className="absolute top-[max(1rem,env(safe-area-inset-top))] right-4 z-20">
        <ThemeToggle />
      </div>

      <main className="flex-1 flex flex-col px-6 pt-[max(4rem,env(safe-area-inset-top))] pb-8 z-10 max-w-md w-full mx-auto">
        <div className="mb-10 mt-4 flex flex-col items-start animate-msg-in">
          <BrandLogo size="lg" />
          <p className="mt-3 text-sm text-muted-foreground">Accedi per gestire i tenant</p>
        </div>

        <form onSubmit={onSubmit} className="space-y-5 animate-msg-in" style={{ animationDelay: '80ms' }}>
          {error && (
            <p className="text-destructive text-sm ml-1" role="alert">
              {error}
            </p>
          )}

          <div className="space-y-2">
            <Label htmlFor="username" className="text-[10px] uppercase tracking-widest text-muted-foreground ml-1 font-bold">
              Username
            </Label>
            <input
              id="username"
              type="text"
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="admin"
              className="w-full bg-surface-2 border border-border rounded-2xl py-4 px-5 text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/25 transition-[border-color,box-shadow]"
              disabled={loading}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="text-[10px] uppercase tracking-widest text-muted-foreground ml-1 font-bold">
              Password
            </Label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full bg-surface-2 border border-border rounded-2xl py-4 pl-5 pr-14 text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/25 transition-[border-color,box-shadow]"
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'Nascondi password' : 'Mostra password'}
                className="absolute inset-y-0 right-2 my-auto h-9 w-9 flex items-center justify-center rounded-xl text-muted-foreground hover:text-foreground hover:bg-surface-3 transition-colors"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="apiKey" className="text-[10px] uppercase tracking-widest text-muted-foreground ml-1 font-bold">
              Admin API Key
            </Label>
            <input
              id="apiKey"
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder="Deve corrispondere a ADMIN_API_KEY sul backend"
              className="w-full bg-surface-2 border border-border rounded-2xl py-4 px-5 text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/25 transition-[border-color,box-shadow]"
              disabled={loading}
            />
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="remember"
              checked={rememberMe}
              onCheckedChange={(v: boolean | 'indeterminate') => setRememberMe(Boolean(v))}
            />
            <Label htmlFor="remember" className="text-sm font-normal">
              Ricordami su questo dispositivo
            </Label>
          </div>

          <button
            type="submit"
            disabled={loading || !password}
            className="group w-full bg-brand hover:bg-brand-glow text-brand-foreground font-bold py-5 rounded-2xl transition-colors flex items-center justify-center gap-2 active:scale-[0.98] disabled:opacity-70"
          >
            {loading ? 'Accesso...' : 'Accedi'}
            <ArrowRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" strokeWidth={3} />
          </button>
        </form>
      </main>

      <footer className="px-8 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-4 text-center z-10 space-y-1.5">
        <p className="text-muted-foreground/70 text-[10px] leading-relaxed">
          Developed with <span className="text-brand">♥</span> by{' '}
          <a
            href="https://replya.app"
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-foreground/80 hover:text-brand transition-colors"
          >
            Replya
          </a>
        </p>
      </footer>
    </div>
  );
}
